/**
 * The week of daily spend behind the budget chart.
 *
 * GET /api/transactions/daily returns one row per calendar day in a date range,
 * zero-filled server-side so the client always has exactly `days` entries in date
 * order — a day with no receipts is 0, never a gap the chart would misread. The
 * total is every transaction that day, categorized AND uncategorized, so a bar is
 * the honest money-out-the-door for that day, not a per-category slice.
 *
 * NOTE for the integrator: DaySpend belongs in src/shared/types.ts (module agents
 * can't edit it). It is the response of GET /api/transactions/daily. Same convention
 * as TransactionSummary in ./summary.ts.
 */
import { api } from '../../lib/api';
import { todayISO } from './dates';

export interface DaySpend {
  /** YYYY-MM-DD, the family's local calendar day. */
  date: string;
  /** cents; positive = money out, matching the transactions schema. */
  total: number;
}

/** Monday (local) of the week containing `iso` (YYYY-MM-DD). Weeks run Mon–Sun. */
export function mondayOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const dow = date.getDay(); // 0 = Sunday
  date.setDate(date.getDate() - ((dow + 6) % 7));
  return todayISO(date);
}

/** One `{ date, total }` per day in `[start, start+days)`, zero-filled, in date order. */
export function fetchWeekSpend(start: string, days = 7): Promise<DaySpend[]> {
  return api.get<DaySpend[]>(`/transactions/daily?start=${start}&days=${days}`);
}
